import React, {useState} from 'react';
import {PlusIcon, TrashIcon} from '../icons';
import {
    TripChecklist,
    TripChecklistCategory,
    TripChecklistItem,
    TRIP_CHECKLIST_CATEGORY_LABELS,
} from '../../service/TripApi';

interface Props {
    checklist: TripChecklist;
    onToggle: (item: TripChecklistItem, done: boolean) => void;
    onAdd: (category: TripChecklistCategory, title: string) => void;
    onDelete: (item: TripChecklistItem) => void;
    busyId?: string | null;
}

const CATEGORY_OPTIONS: TripChecklistCategory[] = ['DOCUMENTS', 'MONEY', 'TECH', 'HYGIENE', 'CLOTHING', 'OTHER'];

/**
 * Чек-лист сборов, сгруппированный по категориям.
 *
 * <p>Одежда не дублируется пунктами: она показана одной строкой со счётчиком из списка
 * вещей — иначе «упаковано» жило бы в двух местах и разошлось.</p>
 */
const TripChecklistSection: React.FC<Props> = ({checklist, onToggle, onAdd, onDelete, busyId}) => {
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState<TripChecklistCategory>('OTHER');

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim()) {
            return;
        }
        onAdd(category, title.trim());
        setTitle('');
    };

    return (
        <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300" data-testid="trip-checklist-progress">
                Выполнено {checklist.doneCount} из {checklist.totalItems} ({checklist.donePercent}%)
            </p>

            <p className="text-sm text-gray-600 dark:text-gray-300 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/40"
               data-testid="trip-checklist-clothing">
                Одежда из плана: упаковано {checklist.clothingPacked} из {checklist.clothingTotal} — отмечается
                в блоке «Вещи».
            </p>

            {checklist.groups.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="trip-checklist-empty">
                    Чек-лист пуст. Добавьте пункт — например, «Виза».
                </p>
            ) : (
                checklist.groups.map(group => (
                    <section key={group.category} className="space-y-2">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {TRIP_CHECKLIST_CATEGORY_LABELS[group.category]} · {group.doneCount}/{group.totalItems}
                        </h3>
                        <ul className="grid gap-2">
                            {group.items.map(item => (
                                <li key={item.id}
                                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                                    <input
                                        type="checkbox"
                                        checked={item.done}
                                        disabled={busyId === item.id}
                                        onChange={() => onToggle(item, !item.done)}
                                        data-testid={`trip-checklist-toggle-${item.id}`}
                                        aria-label={`Готово: ${item.title}`}
                                        className="w-5 h-5 min-w-[20px] accent-brand-500"
                                    />
                                    <span className={`flex-1 text-sm ${item.done
                                        ? 'line-through text-gray-400 dark:text-gray-500'
                                        : 'text-gray-900 dark:text-gray-100'}`}>
                                        {item.title}
                                    </span>
                                    <button type="button" onClick={() => onDelete(item)}
                                            aria-label={`Удалить пункт ${item.title}`}
                                            data-testid={`trip-checklist-delete-${item.id}`}
                                            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-gray-400 hover:text-red-600 dark:hover:text-red-400">
                                        <TrashIcon className="w-5 h-5"/>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </section>
                ))
            )}

            <form className="flex flex-col sm:flex-row gap-2" onSubmit={submit}>
                <select value={category} aria-label="Категория пункта"
                        data-testid="trip-checklist-category"
                        onChange={e => setCategory(e.target.value as TripChecklistCategory)}
                        className="min-h-[44px] px-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                    {CATEGORY_OPTIONS.map(option => (
                        <option key={option} value={option}>{TRIP_CHECKLIST_CATEGORY_LABELS[option]}</option>
                    ))}
                </select>
                <input type="text" value={title} placeholder="Новый пункт" aria-label="Новый пункт"
                       data-testid="trip-checklist-title"
                       onChange={e => setTitle(e.target.value)}
                       className="flex-1 min-h-[44px] px-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"/>
                <button type="submit" data-testid="trip-checklist-add"
                        className="flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200">
                    <PlusIcon className="w-4 h-4"/>
                    Добавить
                </button>
            </form>
        </div>
    );
};

export default TripChecklistSection;
